import React, { useState, useContext, useRef } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  AlertTriangle, Send, Loader2, CheckCircle, ArrowLeft,
  ShieldAlert, Flag, UserX, FileWarning, Upload, X, FileImage, FileText
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Footer from '@/components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const ComplaintsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [complaintNumber, setComplaintNumber] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    category: searchParams.get('category') || '',
    subject: '',
    description: '',
    reported_user_id: searchParams.get('user_id') || '',
    reported_review_id: searchParams.get('review_id') || '',
    evidence: ''
  });

  const categories = [
    { 
      value: 'comportamento_scorretto', 
      label: 'Comportamento scorretto',
      icon: <UserX className="w-5 h-5" />,
      description: 'Linguaggio inappropriato, molestie, spam'
    },
    { 
      value: 'recensione_inappropriata', 
      label: 'Recensione inappropriata',
      icon: <FileWarning className="w-5 h-5" />,
      description: 'Recensione falsa, diffamatoria o non veritiera'
    },
    { 
      value: 'frode', 
      label: 'Sospetta frode',
      icon: <ShieldAlert className="w-5 h-5" />,
      description: 'Truffe, false identità, richieste di pagamento sospette'
    },
    { 
      value: 'altro', 
      label: 'Altro',
      icon: <Flag className="w-5 h-5" />,
      description: 'Altre violazioni dei termini di servizio'
    }
  ];

  // Handle file upload
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Validate files
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    
    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Tipo file non supportato. Usa JPG, PNG, WebP o PDF.`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: File troppo grande. Massimo 5MB.`);
        return false;
      }
      return true;
    });
    
    if (validFiles.length === 0) return;
    if (attachedFiles.length + validFiles.length > 5) {
      toast.error('Puoi allegare massimo 5 file');
      return;
    }
    
    setUploadingFiles(true);
    
    try {
      const uploadPromises = validFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/api/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        return {
          name: file.name,
          url: response.data.url,
          type: file.type,
          size: file.size
        };
      });
      
      const uploadedFiles = await Promise.all(uploadPromises);
      setAttachedFiles(prev => [...prev, ...uploadedFiles]);
      toast.success(`${uploadedFiles.length} file caricati con successo`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Errore nel caricamento dei file');
    } finally {
      setUploadingFiles(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.subject || !formData.description) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        attachments: attachedFiles.map(f => ({ name: f.name, url: f.url, type: f.type }))
      };
      
      const response = await axios.post(`${API}/api/complaints`, submitData);
      
      setComplaintNumber(response.data.complaint_number);
      setSubmitted(true);
      toast.success('Segnalazione inviata con successo!');
    } catch (error) {
      console.error('Error submitting complaint:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della segnalazione');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-8 text-center">
            <AlertTriangle className="w-12 h-12 text-[#E8A007] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#344B71] mb-2">Accesso Richiesto</h2>
            <p className="text-slate-600 mb-6">
              Devi effettuare l'accesso per inviare una segnalazione.
            </p>
            <Button
              onClick={() => navigate('/auth')}
              className="bg-[#344B71] hover:bg-[#2a3d5c] text-white"
            >
              Accedi
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-2xl mx-auto px-6 py-20">
          <Card className="text-center">
            <CardContent className="pt-12 pb-8">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#344B71] mb-4">
                Segnalazione Inviata
              </h2>
              <p className="text-slate-600 mb-6">
                Abbiamo ricevuto la tua segnalazione e la esamineremo con la massima attenzione.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
                <p className="text-sm text-amber-800 mb-1">Numero Segnalazione</p>
                <p className="text-2xl font-bold text-amber-900">{complaintNumber}</p>
              </div>
              <p className="text-sm text-slate-500 mb-6">
                Conserva questo numero per riferimento. Ti informeremo sull'esito dell'indagine.
              </p>
              <div className="flex gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="border-[#344B71] text-[#344B71]"
                >
                  Vai alla Dashboard
                </Button>
                <Button
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      category: '',
                      subject: '',
                      description: '',
                      reported_user_id: '',
                      reported_review_id: '',
                      evidence: ''
                    });
                  }}
                  className="bg-[#344B71] hover:bg-[#2a3d5c] text-white"
                >
                  Nuova Segnalazione
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6 text-white/80 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/10 rounded-xl">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Segnala un Problema</h1>
              <p className="text-white/80">Aiutaci a mantenere la community sicura</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Info Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Come funziona</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-[#344B71] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium text-[#344B71]">Descrivi il problema</h4>
                    <p className="text-sm text-slate-600">Fornisci dettagli sulla situazione</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-[#344B71] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium text-[#344B71]">Esaminiamo la segnalazione</h4>
                    <p className="text-sm text-slate-600">Il nostro team verifica i fatti</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-[#344B71] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium text-[#344B71]">Prendiamo provvedimenti</h4>
                    <p className="text-sm text-slate-600">Ti comunichiamo l'esito</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Warning Box - Now separate Card to prevent overlap */}
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                  Importante
                </h4>
                <p className="text-sm text-red-700">
                  Le segnalazioni false o malevole sono una violazione dei nostri termini di servizio 
                  e possono comportare la sospensione dell'account.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Invia Segnalazione</CardTitle>
                <CardDescription>
                  Compila il form con tutte le informazioni necessarie
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-3">
                      Tipo di segnalazione *
                    </label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {categories.map((cat) => (
                        <div
                          key={cat.value}
                          onClick={() => setFormData({...formData, category: cat.value})}
                          className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            formData.category === cat.value
                              ? 'border-[#344B71] bg-[#344B71]/5'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`${formData.category === cat.value ? 'text-[#344B71]' : 'text-slate-400'}`}>
                              {cat.icon}
                            </div>
                            <span className="font-medium text-[#344B71]">{cat.label}</span>
                          </div>
                          <p className="text-xs text-slate-500">{cat.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Oggetto della segnalazione *
                    </label>
                    <Input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      placeholder="Es: Recensione diffamatoria ricevuta"
                      className="h-12"
                    />
                  </div>

                  {/* Reported User ID (optional) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      ID Utente Segnalato (opzionale)
                    </label>
                    <Input
                      type="text"
                      value={formData.reported_user_id}
                      onChange={(e) => setFormData({...formData, reported_user_id: e.target.value})}
                      placeholder="Se disponibile, inserisci l'ID dell'utente"
                      className="h-12"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Puoi trovare l'ID nella URL del profilo dell'utente
                    </p>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Descrizione dettagliata *
                    </label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Descrivi nel dettaglio cosa è successo, quando è successo e chi è coinvolto..."
                      rows={6}
                      className="resize-none"
                    />
                  </div>

                  {/* Evidence */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Prove o riferimenti (opzionale)
                    </label>
                    <Textarea
                      value={formData.evidence}
                      onChange={(e) => setFormData({...formData, evidence: e.target.value})}
                      placeholder="Descrivi eventuali prove: screenshot, link a messaggi, date specifiche..."
                      rows={3}
                      className="resize-none"
                    />
                  </div>

                  {/* File Attachments */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Allega prove (opzionale)
                    </label>
                    <p className="text-xs text-slate-500 mb-3">
                      Puoi allegare fino a 5 file (immagini JPG, PNG, WebP o PDF). Max 5MB per file.
                    </p>
                    
                    {/* Upload Button */}
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                        id="file-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingFiles || attachedFiles.length >= 5}
                        className="border-dashed"
                      >
                        {uploadingFiles ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Caricamento...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Carica file
                          </>
                        )}
                      </Button>
                      <span className="text-xs text-slate-500">
                        {attachedFiles.length}/5 file allegati
                      </span>
                    </div>

                    {/* Attached Files List */}
                    {attachedFiles.length > 0 && (
                      <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                        {attachedFiles.map((file, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between p-2 bg-white rounded border"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {file.type.startsWith('image/') ? (
                                <FileImage className="w-4 h-4 text-blue-500 flex-shrink-0" />
                              ) : (
                                <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                              )}
                              <span className="text-sm truncate">{file.name}</span>
                              <span className="text-xs text-slate-400 flex-shrink-0">
                                ({formatFileSize(file.size)})
                              </span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFile(index)}
                              className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Invia Segnalazione
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ComplaintsPage;
